import { useState, useCallback, useMemo, useRef } from 'react';
import { Calendar, Users, Trophy } from 'lucide-react';
import useRailFilters from './useRailFilters';
import { getCurrentNFLWeek } from '../../../../pages/sport-dashboards/NFL/utils/nflWeekDetector';

/**
 * useNFLRailFilters - NFL-specific rail filter configuration
 * Provides tabs, filters, and handlers for NFL dashboard
 * 
 * @param {Function} onFiltersChange - Callback when filters change
 * @param {string} initialDate - Initial date value (ISO string)
 * @param {Object} initialTeam - Initial team value
 */
const useNFLRailFilters = ({
  onFiltersChange,
  initialDate,
  initialTeam = null
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [showDateSheet, setShowDateSheet] = useState(false);
  const [showTeamSheet, setShowTeamSheet] = useState(false);
  const [showWeekSheet, setShowWeekSheet] = useState(false);
  const [showStatusSheet, setShowStatusSheet] = useState(false);

  // Capture the caller-provided defaults exactly once so resets stay consistent
  const initialDateRef = useRef(initialDate || null);
  const initialTeamRef = useRef(initialTeam);
  
  // Default filter values - captured once on mount, never change
  const defaultFilters = useMemo(() => {
    // Auto-detect current NFL week based on today's date
    const currentWeekInfo = getCurrentNFLWeek();
    const shouldDefaultToWeek = !initialDateRef.current;

    return {
      date: initialDateRef.current,  // Respect explicit initial date when provided
      team: initialTeamRef.current,
      conference: 'all',
      division: 'all',
      week: shouldDefaultToWeek ? currentWeekInfo.weekId : null,
      seasonType: currentWeekInfo.seasonType,  // REG or POST
      seasonYear: currentWeekInfo.seasonYear,  // Correct season year
      status: 'all'  // Add status filter (all, live, upcoming)
    };
  }, []); // Empty deps - capture initial values only once!
  
  // Use base rail filters hook
  const {
    filters,
    updateFilter,
    resetFilters,
    hasActiveFilters,
    buildFilterObjects
  } = useRailFilters({
    sport: 'nfl',
    defaultFilters,
    onFiltersChange
  });
  
  // Format date for display
  const formatDateLabel = useCallback((dateStr) => {
    // If week is selected, date should show as unselected
    if (filters.week) return 'Select Date';
    if (!dateStr) return 'Select Date';
    
    // Handle if dateStr is actually a Date object
    if (dateStr instanceof Date) {
      const year = dateStr.getFullYear();
      const month = String(dateStr.getMonth() + 1).padStart(2, '0');
      const day = String(dateStr.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    
    // Validate the date string format
    if (typeof dateStr !== 'string' || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      console.warn('Invalid date format:', dateStr);
      return 'Select Date';
    }
    
    // Parse the date string correctly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);
    
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    
    // Compare dates properly
    const dateCompare = new Date(date);
    dateCompare.setHours(12, 0, 0, 0);
    
    if (dateCompare.getTime() === today.getTime()) {
      return 'Today';
    } else if (dateCompare.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = days[date.getDay()];
      const monthNum = date.getMonth() + 1;
      const dayNum = date.getDate();
      return `${dayName} ${monthNum}/${dayNum}`;
    }
  }, []);
  
  // Format team for display
  const formatTeamLabel = useCallback((team) => {
    if (!team) return 'All Teams';
    if (typeof team === 'object') return team.name || team.displayName || 'Team';
    return team;
  }, []);
  
  // Format week for display
  const formatWeekLabel = useCallback((week) => {
    if (!week) return 'All Weeks';
    
    // Handle special playoff weeks
    const specialWeeks = {
      'preseason': 'Preseason',
      'wildcard': 'Wild Card',
      'divisional': 'Divisional',
      'conference': 'Conference',
      'superbowl': 'Super Bowl'
    };
    
    if (specialWeeks[week]) return specialWeeks[week];
    
    // Handle regular season weeks (week1, week2, etc.)
    if (week.startsWith('week')) {
      const weekNum = week.replace('week', '');
      return `Week ${weekNum}`;
    }
    
    return week;
  }, []);
  
  // Primary tabs configuration - NFL conferences and divisions
  const primaryTabs = useMemo(() => {
    return [
      { id: 'all', label: 'All Games' },
      { id: 'afc', label: 'AFC' },
      { id: 'nfc', label: 'NFC' },
      { id: 'afc-east', label: 'AFC East' },
      { id: 'afc-north', label: 'AFC North' },
      { id: 'afc-south', label: 'AFC South' },
      { id: 'afc-west', label: 'AFC West' },
      { id: 'nfc-east', label: 'NFC East' },
      { id: 'nfc-north', label: 'NFC North' },
      { id: 'nfc-south', label: 'NFC South' },
      { id: 'nfc-west', label: 'NFC West' }
    ];
  }, []);
  
  // Format status for display
  const formatStatusLabel = useCallback((status) => {
    const statusLabels = {
      'all': 'All Games',
      'live': 'Live',
      'upcoming': 'Upcoming'
    };
    return statusLabels[status] || 'All Games';
  }, []);

  // Secondary filter configuration
  const filterConfig = useMemo(() => {
    const configs = [
      {
        id: 'status',
        type: 'select',
        getLabel: formatStatusLabel,
        hasDropdown: true,
        isActive: filters.status !== 'all'
      },
      {
        id: 'week',
        type: 'select',
        getLabel: formatWeekLabel,
        hasDropdown: true,
        isActive: !!filters.week  // Show active state only if week is selected
      },
      {
        id: 'date',
        type: 'date',
        getLabel: formatDateLabel,
        hasDropdown: true,
        isActive: !!filters.date && !filters.week  // Active only if date selected and no week
      }
    ];
    
    // Only show team chip if "All Games" is selected
    if (activeTab === 'all') {
      configs.push({
        id: 'team',
        type: 'select',
        getLabel: formatTeamLabel,
        hasDropdown: true
      });
    }
    
    return configs;
  }, [activeTab, formatDateLabel, formatTeamLabel, formatWeekLabel]);
  
  // Build filter objects for SecondaryRail
  const filterObjects = buildFilterObjects(filterConfig);
  
  // Handle filter chip click
  const handleFilterClick = useCallback((filterId) => {
    switch (filterId) {
      case 'status':
        setShowStatusSheet(true);
        break;
      case 'week':
        setShowWeekSheet(true);
        break;
      case 'date':
        setShowDateSheet(true);
        break;
      case 'team':
        setShowTeamSheet(true);
        break;
      default:
        break;
    }
  }, []);
  
  // Handle primary tab change
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    
    // Update conference/division filters based on tab selection
    if (tabId === 'all') {
      updateFilter('conference', 'all');
      updateFilter('division', 'all');
    } else if (tabId === 'afc' || tabId === 'nfc') {
      updateFilter('conference', tabId);
      updateFilter('division', 'all');
    } else if (tabId.includes('-')) {
      // Division tabs like 'afc-east'
      const [conference, division] = tabId.split('-');
      updateFilter('conference', conference);
      updateFilter('division', division);
    }
  }, [updateFilter]);
  
  return {
    // State
    filters,
    activeTab,
    hasActiveFilters,
    
    // Tabs and filters
    primaryTabs,
    filterObjects,
    
    // Handlers
    handleTabChange,
    handleFilterClick,
    updateFilter,
    resetFilters,
    
    // Sheet visibility
    showDateSheet,
    setShowDateSheet,
    showTeamSheet,
    setShowTeamSheet,
    showWeekSheet,
    setShowWeekSheet,
    showStatusSheet,
    setShowStatusSheet
  };
};

export default useNFLRailFilters;
